/**
 * reportPaymentError.ts — Capture Sentry des erreurs de paiement front.
 *
 * SPEC-2026-06-30-sentry-error-wiring — câblage des catches P0 paiement.
 *
 * Pourquoi : l'init Sentry front (src/index.tsx) ne capture aujourd'hui QUE les
 * crashs de rendu (Sentry.ErrorBoundary racine). Tout `catch` de paiement (Stripe
 * confirm, PayPal capture, crypto confirm…) est invisible. Ce helper rend visibles
 * les échecs APRÈS encaissement (`moneyTaken: true` = argent pris, asset peut-être
 * non livré → priorité absolue côté observabilité).
 *
 * §5/§9 DRY — un seul point d'entrée importé partout.
 * §107 — ne throw JAMAIS (ne doit pas casser le flux argent/asset) ; console.error
 *        reste la source de vérité même si Sentry est down.
 */

import * as Sentry from '@sentry/react';
import { isPaymentLocked } from './paymentErrors';

export type PaymentProvider = 'stripe' | 'paypal' | 'nowpayments' | 'transak' | 'moonpay';

export interface PaymentErrorContext {
  /** Fournisseur de paiement concerné. */
  provider: PaymentProvider;
  /** Flow logique (ex: 'stripe_confirm', 'paypal_capture', 'crypto_confirm'). */
  flow: string;
  /** Étape optionnelle dans le flow (ex: 'confirm', 'create'). */
  stage?: string;
  /**
   * true si l'argent a déjà été encaissé au moment de l'erreur (asset peut-être non
   * livré). Pose le tag `money_taken:'true'` → alerting prioritaire.
   */
  moneyTaken?: boolean;
}

/**
 * Capture une erreur de paiement vers Sentry, avec tags de contexte.
 *
 * - `console.error` TOUJOURS appelé en premier (source de vérité, §34).
 * - Filtre : si l'erreur est un VERROU DE PAIEMENT (achat concurrent normal, 409),
 *   on ne capture PAS (bruit attendu, pas une anomalie).
 * - Ne throw jamais (try/catch interne, §107).
 *
 * @param err Erreur capturée (unknown — peut être Error, string, réponse Axios…).
 * @param ctx Contexte du paiement (provider, flow, stage?, moneyTaken?).
 */
export function reportPaymentError(err: unknown, ctx: PaymentErrorContext): void {
  // 1. Source de vérité : log local TOUJOURS, même si Sentry est indisponible.
  // eslint-disable-next-line no-console
  console.error(
    `[paymentError] provider=${ctx.provider} flow=${ctx.flow}` +
      (ctx.stage ? ` stage=${ctx.stage}` : '') +
      (ctx.moneyTaken ? ' money_taken=true' : ''),
    err,
  );

  // 2. Filtre verrou paiement : achat concurrent normal (§13), pas une anomalie.
  // `err` est typé unknown ; isPaymentLocked accepte Error|string|null et gère toute
  // valeur en interne (String(error)). Cast explicite pour satisfaire TS (TS2345).
  if (isPaymentLocked(err as Error | string | null)) return;

  // 3. Capture Sentry défensive — ne propage JAMAIS une erreur de la couche Sentry.
  try {
    Sentry.withScope((scope) => {
      scope.setTag('payment_provider', ctx.provider);
      scope.setTag('flow', ctx.flow);
      if (ctx.stage) scope.setTag('stage', ctx.stage);
      if (ctx.moneyTaken) scope.setTag('money_taken', 'true');
      Sentry.captureException(err instanceof Error ? err : new Error(String(err)));
    });
  } catch {
    /* §107 — un échec de capture Sentry ne doit jamais casser le flux paiement. */
  }
}

export default reportPaymentError;
